/**
 * Agentic Workflows Module
 *
 * This module provides email-gated access to advanced agentic workflows:
 * - Deep Research (multi-stage Perplexity research)
 * - Report Formatting (Claude-powered formatting for online audiences)
 * - Spanish Translation (Claude-powered document translation)
 * - Attachment Reading (Claude vision for PDFs and images)
 *
 * Workflows are only enabled when the user is logged in with an authorized email.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAITransactionRecorder } from '../../context/AITransactionContext';

import {
  CHAT_TRANSLATE_BUTTON_ID,
  CHAT_VIDEO_SCRIPT_BUTTON_ID,
} from './constants';
import type {
  DeepResearchSession,
  ResearchReport,
  TranslatableContent,
  WorkflowHelpers,
  VideoScriptSession,
} from './types';
import { INITIAL_DR_SESSION, INITIAL_VIDEO_SCRIPT_SESSION } from './types';

// Re-export types and constants for external use
export * from './types';
export * from './constants';
export * from './helpers';
export * from './handlers';

// ============================================================================
// Authorization
// ============================================================================

/**
 * Check whether the current user may dispatch agentic workflows / chat turns
 * from the client.
 *
 * This is a THIN CLIENT GUARD only — it gates whether the browser bothers to
 * call the orchestrator dispatch path (`createMessageHandler` ->
 * `prest0nRunTurn`). It is NOT the authority on permissions. The authoritative
 * gate lives server-side: `prest0nRunTurn` verifies the Firebase ID token
 * (`verifyFirebaseUser`), resolves the caller's account/tenant
 * (`resolveAccount`), and enforces the payment/RBAC gates. The browser cannot
 * bypass those checks regardless of what this returns.
 *
 * HISTORY / BUGFIX (2026-06-25): this previously hard-checked a single
 * hardcoded allowlist (`['fernandohidalgoesq@yahoo.com']`). That over-gated to
 * the point that EVERY other provisioned tenant's desktop chat silently hung —
 * the dispatch block in `useGlobalChat.ts` was skipped, `prest0nRunTurn` never
 * fired, no gate trace emitted, and the spinner spun until the 10-minute client
 * safety timeout. Fix: authorize any AUTHENTICATED user (non-empty email) and
 * let the server enforce real permissions. Unauthenticated/public surfaces stay
 * gated by the separate `isAuthenticated` conjunct at the call sites. The
 * `AUTHORIZED_EMAILS` constant is retained for legacy imports but is no longer
 * the hard gate.
 */
export function isAgenticWorkflowsAuthorized(email: string | undefined): boolean {
  // Authenticated users only. Real permissions (tenant, payment, RBAC) are
  // enforced server-side in prest0nRunTurn; this is just a client-side
  // "is someone signed in" guard, not a tenant allowlist.
  return typeof email === 'string' && email.trim().length > 0;
}

// ============================================================================
// Hook for Agentic Workflows
// ============================================================================

/**
 * Hook that provides agentic workflow capabilities.
 * Returns authorization status and all necessary state/handlers.
 *
 * Usage in GlobalChatSystem:
 * ```typescript
 * const workflows = useAgenticWorkflows();
 * if (workflows.isAuthorized) {
 *   // Enable agentic workflow features
 * }
 * ```
 */
export function useAgenticWorkflows() {
  const { user, isAuthenticated } = useAuth();
  const { recordTransaction } = useAITransactionRecorder();

  // Check authorization
  const isAuthorized = isAuthenticated && isAgenticWorkflowsAuthorized(user?.email);

  // Deep research session state
  const [drSession, setDrSession] = useState<DeepResearchSession>(INITIAL_DR_SESSION);

  // Ref to always have current drSession (avoids stale closure in handleSend useCallback)
  const drSessionRef = useRef<DeepResearchSession>(drSession);

  // Track the last completed research report for formatting
  const [lastResearchReport, setLastResearchReport] = useState<ResearchReport | null>(null);
  const lastResearchReportRef = useRef<ResearchReport | null>(null);

  // Track if formatting/translating is in progress
  const [isFormatting, setIsFormatting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Track the last content to translate (either research or formatted report)
  const [lastTranslatableContent, setLastTranslatableContent] =
    useState<TranslatableContent | null>(null);
  const lastTranslatableContentRef = useRef<TranslatableContent | null>(null);

  // Ref to store chat helpers for event delegation (button clicks)
  const chatHelpersRef = useRef<WorkflowHelpers | null>(null);

  // Video script session state
  const [videoScriptSession, setVideoScriptSession] = useState<VideoScriptSession>(INITIAL_VIDEO_SCRIPT_SESSION);
  const videoScriptSessionRef = useRef<VideoScriptSession>(videoScriptSession);

  // Track if video script generation is in progress
  const [isGeneratingVideoScript, setIsGeneratingVideoScript] = useState(false);

  // Reset deep research session (sync ref immediately to prevent race conditions)
  const resetDrSession = useCallback(() => {
    drSessionRef.current = INITIAL_DR_SESSION;
    setDrSession(INITIAL_DR_SESSION);
  }, []);

  // Synchronous setter that updates ref BEFORE React processes state update
  // This fixes a bug where user input could arrive before React re-renders,
  // causing stage checks to fail (e.g., stage1_confirm check failing because ref still shows stage1)
  const setDrSessionSync = useCallback((
    updater: DeepResearchSession | ((prev: DeepResearchSession) => DeepResearchSession)
  ) => {
    // Calculate new value using current ref (synchronous)
    const newValue = typeof updater === 'function' ? updater(drSessionRef.current) : updater;

    // Update ref IMMEDIATELY (before React processes)
    drSessionRef.current = newValue;

    // Then schedule the React state update
    setDrSession(newValue);
  }, []);

  // Reset video script session (sync ref immediately to prevent race conditions)
  const resetVideoScriptSession = useCallback(() => {
    videoScriptSessionRef.current = INITIAL_VIDEO_SCRIPT_SESSION;
    setVideoScriptSession(INITIAL_VIDEO_SCRIPT_SESSION);
  }, []);

  // Synchronous setter that updates ref BEFORE React processes state update
  // This fixes a bug where user input could arrive before React re-renders,
  // causing stage checks to fail (e.g., stage1_clarify check failing because ref still shows stage1_analyzing)
  const setVideoScriptSessionSync = useCallback((
    updater: VideoScriptSession | ((prev: VideoScriptSession) => VideoScriptSession)
  ) => {
    // Calculate new value using current ref (synchronous)
    const newValue = typeof updater === 'function' ? updater(videoScriptSessionRef.current) : updater;

    // Update ref IMMEDIATELY (before React processes)
    videoScriptSessionRef.current = newValue;

    // Then schedule the React state update
    setVideoScriptSession(newValue);
  }, []);

  // Sync state to refs (avoids stale closure issues with button click handlers)
  useEffect(() => {
    drSessionRef.current = drSession;
  }, [drSession]);

  useEffect(() => {
    lastResearchReportRef.current = lastResearchReport;
  }, [lastResearchReport]);

  useEffect(() => {
    lastTranslatableContentRef.current = lastTranslatableContent;
  }, [lastTranslatableContent]);

  useEffect(() => {
    videoScriptSessionRef.current = videoScriptSession;
  }, [videoScriptSession]);

  return {
    // Authorization
    isAuthorized,
    userEmail: user?.email,

    // Deep research state
    drSession,
    drSessionRef,
    setDrSession,
    setDrSessionSync,
    resetDrSession,

    // Report state
    lastResearchReport,
    setLastResearchReport,
    lastResearchReportRef,

    // Formatting state
    isFormatting,
    setIsFormatting,

    // Translation state
    isTranslating,
    setIsTranslating,
    lastTranslatableContent,
    setLastTranslatableContent,
    lastTranslatableContentRef,

    // Chat helpers ref
    chatHelpersRef,

    // Video script state
    videoScriptSession,
    videoScriptSessionRef,
    setVideoScriptSession,
    setVideoScriptSessionSync,
    resetVideoScriptSession,
    isGeneratingVideoScript,
    setIsGeneratingVideoScript,

    // Transaction recording
    recordTransaction,

    // Button IDs for event delegation
    CHAT_TRANSLATE_BUTTON_ID,
    CHAT_VIDEO_SCRIPT_BUTTON_ID,
  };
}

/**
 * Type for the return value of useAgenticWorkflows hook.
 */
export type AgenticWorkflowsHook = ReturnType<typeof useAgenticWorkflows>;
